import { User as LucideUser, Settings, Truck } from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";

import { updateUser } from "@/redux/auth/authSlice";
import { authService } from "@/services/authService";
import settingsService from "@/services/settingsService";
import { RootState } from "@/store";

interface DriverProfile {
  personalInfo: {
    address: string;
    city: string;
    dateOfBirth: string;
    email: string;
    emergencyContactName?: string;
    emergencyContactPhone?: string;
    emergencyContactRelationship?: string;
    emergencyContactSurname?: string;
    firstName: string;
    lastName: string;
    phone: string;
    zipCode: string;
  };
  preferences: {
    emailNotifications: boolean;
    emailUpdates: boolean;
    loginAlerts: boolean;
    maxDevices: number;
    notifications: {
      email: boolean;
      push: boolean;
      sms: boolean;
    };
    notificationsEnabled: boolean;
    orderUpdates: boolean;
    preferredAreas: string[];
    promotions: boolean;
    pushNotifications: boolean;
    sessionTimeout: number;
    smsNotifications: boolean;
    systemAlerts: boolean;
    twoFactorAuth: boolean;
    weeklyReports: boolean;
    workingHours: {
      end: string;
      start: string;
    };
  };
  transportInfo: {
    color: string;
    licensePlate: string;
    make: string;
    model: string;
    preferredDeliveryType: string;
    scooterExperience: string;
    transportType: string;
    vin: string;
    workSchedule: string;
    year: string;
  };
}

interface ProfileProps {
  // Add props here if needed
}

const Profile: React.FC<ProfileProps> = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "personal" | "preferences" | "transport"
  >("personal");
  const [newAreaInput, setNewAreaInput] = useState("");
  const [showAddAreaInput, setShowAddAreaInput] = useState(false);

  // Get user data from Redux
  const dispatch = useDispatch();
  const { user } = useSelector(
    (state: RootState) => state.auth,
  ) as RootState["auth"];

  // Check if current tab is editable based on driver status
  const isTabEditable = (): boolean => {
    if (user?.status === "approved" || user?.status === "active") {
      // When approved or active, only preferences can be edited
      const canEdit = activeTab === "preferences";
      return canEdit;
    }
    return true;
  };

  // Validation functions
  const isPersonalInfoValid = () => {
    const { address, city, firstName, lastName, phone, zipCode } =
      profile.personalInfo;
    return (
      firstName.trim() !== "" &&
      lastName.trim() !== "" &&
      phone.trim() !== "" &&
      address.trim() !== "" &&
      city.trim() !== "" &&
      zipCode.trim() !== ""
    );
  };

  const isTransportInfoValid = () => {
    const {
      color,
      licensePlate,
      make,
      model,
      transportType,
      vin,
      workSchedule,
      year,
    } = profile.transportInfo;
    const userRole = user?.role as string;

    // Base required fields for all roles (including VIN)
    const baseFieldsValid =
      make.trim() !== "" &&
      model.trim() !== "" &&
      year.trim() !== "" &&
      color.trim() !== "" &&
      licensePlate.trim() !== "" &&
      transportType.trim() !== "" &&
      workSchedule.trim() !== "" &&
      vin.trim() !== "";

    // Additional validation for ScooterDriver
    if (userRole === "ScooterDriver") {
      return (
        baseFieldsValid && profile.transportInfo.scooterExperience.trim() !== ""
      );
    }

    // Additional validation for preferred delivery type - only for roles that show this field
    const shouldShowPreferredDeliveryType =
      userRole === "DeliveryDriver" ||
      userRole === "ScooterDriver" ||
      (userRole !== "driver" && userRole !== "VehicleDriver");

    if (shouldShowPreferredDeliveryType) {
      return (
        baseFieldsValid &&
        profile.transportInfo.preferredDeliveryType.trim() !== ""
      );
    }

    return baseFieldsValid;
  };

  const [profile, setProfile] = useState<DriverProfile>({
    personalInfo: {
      address: "",
      city: "",
      dateOfBirth: "",
      email: "",
      emergencyContactName: "",
      emergencyContactPhone: "",
      emergencyContactRelationship: "",
      emergencyContactSurname: "",
      firstName: "",
      lastName: "",
      phone: "",
      zipCode: "",
    },
    preferences: {
      emailNotifications: true,
      emailUpdates: true,
      loginAlerts: true,
      maxDevices: 5,
      notifications: {
        email: true,
        push: true,
        sms: true,
      },
      notificationsEnabled: true,
      orderUpdates: true,
      preferredAreas: ["Downtown", "Airport", "Business District"],
      promotions: true,
      pushNotifications: true,
      sessionTimeout: 60,
      smsNotifications: true,
      systemAlerts: true,
      twoFactorAuth: true,
      weeklyReports: true,
      workingHours: {
        end: "16:00",
        start: "09:00",
      },
    },
    transportInfo: {
      color: "",
      licensePlate: "",
      make: "",
      model: "",
      preferredDeliveryType: "",
      scooterExperience: "",
      transportType: "",
      vin: "",
      workSchedule: "",
      year: "",
    },
  });

  // Load fresh preferences from backend
  const loadPreferences = async () => {
    try {
      const freshPrefsResponse = await settingsService.fetchPreferences();
      const freshPrefsRaw =
        freshPrefsResponse.preferences || freshPrefsResponse;
      // Map snake_case backend fields to camelCase frontend fields
      const freshPrefs = {
        emailNotifications: freshPrefsRaw.email_notifications ?? false,
        emailUpdates: freshPrefsRaw.email_updates ?? false,
        loginAlerts: freshPrefsRaw.login_alerts ?? false,
        maxDevices: freshPrefsRaw.max_devices ?? 5,
        notifications: {
          email: freshPrefsRaw.email_notifications ?? false,
          push: freshPrefsRaw.push_notifications ?? false,
          sms: freshPrefsRaw.sms_notifications ?? false,
        },
        notificationsEnabled: freshPrefsRaw.notifications_enabled ?? false,
        orderUpdates: freshPrefsRaw.order_updates ?? false,
        preferredAreas: freshPrefsRaw.preferred_areas ?? [],
        promotions: freshPrefsRaw.promotions ?? false,
        pushNotifications: freshPrefsRaw.push_notifications ?? false,
        sessionTimeout: freshPrefsRaw.session_timeout ?? 60,
        smsNotifications: freshPrefsRaw.sms_notifications ?? false,
        systemAlerts: freshPrefsRaw.system_alerts ?? false,
        twoFactorAuth: freshPrefsRaw.two_factor_auth ?? false,
        weeklyReports: freshPrefsRaw.weekly_reports ?? false,
        workingHours: {
          end: freshPrefsRaw.working_hours_end ?? "16:00",
          start: freshPrefsRaw.working_hours_start ?? "09:00",
        },
      };
      setProfile((prev) => ({
        ...prev,
        preferences: freshPrefs,
      }));
      // Optionally update Redux store if needed
    } catch (error) {
      console.error("Error loading preferences:", error);
    }
  };

  // Load preferences on component mount
  useEffect(() => {
    if (user?.id) {
      loadPreferences();
    }
  }, [user?.id]);

  // Update profile when user data changes
  useEffect(() => {
    if (user) {
      setProfile((prev) => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          address: user.address || prev.personalInfo.address || "",
          city: user.city || prev.personalInfo.city || "",
          dateOfBirth: user.dateOfBirth || prev.personalInfo.dateOfBirth || "",
          email: user.email || "",
          emergencyContactName: user.emergency_contact_name || "",
          emergencyContactPhone: user.emergency_contact_phone || "",
          emergencyContactRelationship:
            user.emergency_contact_relationship || "",
          emergencyContactSurname: user.emergency_contact_surname || "",
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          phone: user.phone || prev.personalInfo.phone || "",
          zipCode: user.zipCode || prev.personalInfo.zipCode || "",
        },
        preferences: {
          ...prev.preferences,
          notifications: {
            email:
              user.preferences?.notifications?.email ??
              prev.preferences.notifications.email,
            push:
              user.preferences?.notifications?.push ??
              prev.preferences.notifications.push,
            sms:
              user.preferences?.notifications?.sms ??
              prev.preferences.notifications.sms,
          },
          preferredAreas:
            user.preferences?.preferredAreas ?? prev.preferences.preferredAreas,
          workingHours: {
            end:
              user.preferences?.workingHours?.end ??
              prev.preferences.workingHours.end,
            start:
              user.preferences?.workingHours?.start ??
              prev.preferences.workingHours.start,
          },
        },
        transportInfo: {
          color: user.transportInfo?.color ?? prev.transportInfo.color ?? "",
          licensePlate:
            user.transportInfo?.licensePlate ??
            prev.transportInfo.licensePlate ??
            "",
          make: user.transportInfo?.make ?? prev.transportInfo.make ?? "",
          model: user.transportInfo?.model ?? prev.transportInfo.model ?? "",
          preferredDeliveryType:
            user.transportInfo?.preferredDeliveryType ??
            prev.transportInfo.preferredDeliveryType ??
            "",
          scooterExperience:
            user.transportInfo?.scooterExperience ??
            prev.transportInfo.scooterExperience ??
            "",
          transportType:
            user.transportInfo?.transportType ??
            prev.transportInfo.transportType ??
            "",
          vin: user.transportInfo?.vin ?? prev.transportInfo.vin ?? "",
          workSchedule:
            user.transportInfo?.workSchedule ??
            prev.transportInfo.workSchedule ??
            "",
          year: user.transportInfo?.year ?? prev.transportInfo.year ?? "",
        },
      }));
    } 
  }, [user]);

  // Load saved profile data from localStorage
  useEffect(() => {
    if (user?.id) {
      try {
        const savedProfile = localStorage.getItem(`user_profile_${user.id}`);
        if (savedProfile) {
          const profileData = JSON.parse(savedProfile);

          setProfile((prev) => ({
            ...prev,
            personalInfo: {
              ...prev.personalInfo,
              ...profileData.personalInfo,
              // Keep the real user data for core fields
              email: user.email || prev.personalInfo.email,
              firstName: user.firstName || prev.personalInfo.firstName,
              lastName: user.lastName || prev.personalInfo.lastName,
            },
            preferences: {
              ...prev.preferences,
              ...profileData.preferences,
            },
            transportInfo: {
              ...prev.transportInfo,
              ...profileData.transportInfo,
            },
          }));
        }
      } catch (error) {
        console.error("Error loading saved profile:", error);
      }
    }
  }, [user?.id]);

  // Save personal information only
  const handleSavePersonal = async () => {
    const dataToSend = {
      address: profile.personalInfo.address,
      city: profile.personalInfo.city,
      dateOfBirth: profile.personalInfo.dateOfBirth,
      emergency_contact_name: profile.personalInfo.emergencyContactName,
      emergency_contact_phone: profile.personalInfo.emergencyContactPhone,
      emergency_contact_relationship:
        profile.personalInfo.emergencyContactRelationship,
      emergency_contact_surname: profile.personalInfo.emergencyContactSurname,
      firstName: profile.personalInfo.firstName,
      lastName: profile.personalInfo.lastName,
      phone: profile.personalInfo.phone,
      zipCode: profile.personalInfo.zipCode,
    };

    try {
      const loadingToast = toast.loading("Saving personal information...");

      await authService.updatePersonalInfo(dataToSend);

      // Fetch fresh data from database to ensure consistency
      const freshUserData = await authService.getProfile();

      // Update Redux store with fresh data
      dispatch(updateUser(freshUserData));

      // Update local state with fresh data from database
      setProfile((prev) => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          address: freshUserData.address || prev.personalInfo.address,
          city: freshUserData.city || prev.personalInfo.city,
          dateOfBirth: freshUserData.dateOfBirth || prev.personalInfo.dateOfBirth,
          email: freshUserData.email || prev.personalInfo.email,
          emergencyContactName: freshUserData.emergency_contact_name || prev.personalInfo.emergencyContactName,
          emergencyContactPhone: freshUserData.emergency_contact_phone || prev.personalInfo.emergencyContactPhone,
          emergencyContactRelationship: freshUserData.emergency_contact_relationship || prev.personalInfo.emergencyContactRelationship,
          emergencyContactSurname: freshUserData.emergency_contact_surname || prev.personalInfo.emergencyContactSurname,
          firstName: freshUserData.firstName || prev.personalInfo.firstName,
          lastName: freshUserData.lastName || prev.personalInfo.lastName,
          phone: freshUserData.phone || prev.personalInfo.phone,
          zipCode: freshUserData.zipCode || prev.personalInfo.zipCode,
        },
      }));

      toast.success("Personal information saved successfully!", {
        duration: 3000,
        id: loadingToast,
      });
    } catch (error) {
      console.error("Error saving personal info:", error);

      if (error instanceof Error && error.message.includes("Session expired")) {
        toast.error("Your session has expired. Please log in again.", {
          duration: 6000,
        });
        // Optionally redirect to login
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      } else {
        const errorMessage = error instanceof Error ? error.message : "Failed to save personal information. Please try again.";
        toast.error(errorMessage, {
          duration: 4000,
        });
      }
    }
  };

  // Save transport information only
  const handleSaveTransport = async () => {

    try {
      const loadingToast = toast.loading("Saving transport information...");

      await authService.updateTransportInfo(profile.transportInfo);

      // Fetch fresh data from database to ensure consistency
      const freshUserData = await authService.getProfile();

      // Update Redux store with fresh data
      dispatch(updateUser(freshUserData));

      // Update local state with fresh transport data from database
      if (freshUserData.transportInfo) {
        const freshTransportInfo = freshUserData.transportInfo;
        setProfile((prev) => ({
          ...prev,
          transportInfo: {
            ...prev.transportInfo,
            color: freshTransportInfo.color || prev.transportInfo.color,
            licensePlate: freshTransportInfo.licensePlate || prev.transportInfo.licensePlate,
            make: freshTransportInfo.make || prev.transportInfo.make,
            model: freshTransportInfo.model || prev.transportInfo.model,
            preferredDeliveryType: freshTransportInfo.preferredDeliveryType || prev.transportInfo.preferredDeliveryType,
            scooterExperience: freshTransportInfo.scooterExperience || prev.transportInfo.scooterExperience,
            transportType: freshTransportInfo.transportType || prev.transportInfo.transportType,
            vin: freshTransportInfo.vin || prev.transportInfo.vin,
            workSchedule: freshTransportInfo.workSchedule || prev.transportInfo.workSchedule,
            year: freshTransportInfo.year || prev.transportInfo.year,
          },
        }));
      }

      toast.success("Transport information saved successfully!", {
        duration: 3000,
        id: loadingToast,
      });
    } catch (error) {
      console.error("Error saving transport info:", error);

      if (error instanceof Error && error.message.includes("Session expired")) {
        toast.error("Your session has expired. Please log in again.", {
          duration: 6000,
        });
        // Optionally redirect to login
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      } else {
        const errorMessage = error instanceof Error ? error.message : "Failed to save transport information. Please try again.";
        toast.error(errorMessage, {
          duration: 4000,
        });
      }
    }
  };

  // Save preferences only
  const handleSavePreferences = async () => {

    try {
      const loadingToast = toast.loading("Saving preferences...");

      // Transform the preferences to match the API structure
      const preferencesData = {
        email_notifications: profile.preferences.emailNotifications,
        email_updates: profile.preferences.emailUpdates,
        login_alerts: profile.preferences.loginAlerts,
        max_devices: profile.preferences.maxDevices,
        notifications: {
          email: profile.preferences.notifications.email,
          push: profile.preferences.notifications.push,
          sms: profile.preferences.notifications.sms,
        },
        notifications_enabled: profile.preferences.notificationsEnabled,
        order_updates: profile.preferences.orderUpdates,
        preferred_areas: profile.preferences.preferredAreas,
        promotions: profile.preferences.promotions,
        push_notifications: profile.preferences.pushNotifications,
        session_timeout: profile.preferences.sessionTimeout,
        sms_notifications: profile.preferences.smsNotifications,
        system_alerts: profile.preferences.systemAlerts,
        two_factor_auth: profile.preferences.twoFactorAuth,
        weekly_reports: profile.preferences.weeklyReports,
        working_hours_end: profile.preferences.workingHours.end,
        working_hours_start: profile.preferences.workingHours.start,
        workingHours: {
          end: profile.preferences.workingHours.end,
          start: profile.preferences.workingHours.start,
        },
      };

      const updatedPrefs =
        await settingsService.updatePreferences(preferencesData);

      // Update local state with fresh data from API if we got user data back
      if (updatedPrefs) {
        setProfile((prev) => ({
          ...prev,
          preferences: {
            ...prev.preferences,
            ...updatedPrefs,
          },
        }));
      }

      toast.success("Preferences saved successfully!", {
        duration: 3000,
        id: loadingToast,
      });
    } catch (error) {
      console.error("Error saving preferences:", error);

      if (error instanceof Error && error.message.includes("Session expired")) {
        toast.error("Your session has expired. Please log in again.", {
          duration: 6000,
        });
        // Optionally redirect to login
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      } else {
        const errorMessage = error instanceof Error ? error.message : "Failed to save preferences. Please try again.";
        toast.error(errorMessage, {
          duration: 4000,
        });
      }
    }
  };

  // General save function (saves all sections)
  const handleSaveAll = async () => {
    try {
      await Promise.all([
        handleSavePersonal(),
        handleSaveTransport(),
        handleSavePreferences(),
      ]);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving complete profile:", error);
    }
  };

  const handleInputChange = (
    section: keyof DriverProfile,
    field: string,
    value: any,
  ) => {

    setProfile((prev) => {
      const newProfile = {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      };

      return newProfile;
    });
  };

  const tabs = [
    {
      icon: <LucideUser className="h-5 w-5" />,
      id: "personal" as const,
      name: "Personal Info",
    },
    {
      icon: <Truck className="h-5 w-5" />,
      id: "transport" as const,
      name: "Transport",
    },
    {
      icon: <Settings className="h-5 w-5" />,
      id: "preferences" as const,
      name: "Preferences",
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
        <div>
          <h1 className="mb-1.5 text-2xl font-bold text-gray-900 dark:text-gray-100 sm:mb-2 sm:text-3xl">
            Profile Settings
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
            Manage your personal information and preferences.
          </p>
          {(user?.status === "approved" || user?.status === "active") && activeTab !== "preferences" && (
            <p className="mt-2 text-xs text-blue-600 dark:text-blue-400 sm:text-sm">
              ℹ️ Your account is {user?.status}. You can only edit preferences. Contact support to modify personal or transport information.
            </p>
          )}
        </div>
        <div className="flex gap-3">
          {isEditing ? (
            <button
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 sm:px-4 sm:py-2 sm:text-sm"
              onClick={() => setIsEditing(false)}
            >
              Don't Update
            </button>
          ) : (
            <button
              className={`rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium sm:px-4 sm:py-2 sm:text-sm ${
                isTabEditable()
                  ? "bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                  : "cursor-not-allowed bg-gray-100 text-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-500"
              }`}
              disabled={!isTabEditable()}
              onClick={() => setIsEditing(true)}
            >
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Profile Header */}
      <div className="mb-6 rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:mb-8 sm:p-6">
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
          <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 sm:h-20 sm:w-20">
            <LucideUser className="h-8 w-8 text-blue-600 dark:text-blue-400 sm:h-10 sm:w-10" />
          </div>
          <div className="text-center sm:ml-6 sm:text-left">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 sm:text-2xl">
              {profile.personalInfo.firstName} {profile.personalInfo.lastName}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
              {profile.personalInfo.email}
            </p>
            <div className="mt-2 flex flex-col items-center gap-2 sm:flex-row sm:items-center sm:space-x-4">
              <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
                {user?.role
                  ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
                  : "Driver"}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                Member since{" "}
                {user?.createdAt
                  ? new Date(user.createdAt).toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })
                  : "January 2024"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-lg bg-white shadow-md dark:bg-gray-800">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex overflow-x-auto px-4 sm:space-x-8 sm:px-6">
            {tabs.map((tab) => (
              <button
                className={`flex flex-shrink-0 items-center space-x-1.5 border-b-2 px-2 py-3 text-xs font-medium sm:space-x-2 sm:px-1 sm:py-4 sm:text-sm ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300"
                }`}
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}
                <span className="whitespace-nowrap">{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-4 sm:p-6">
          {/* Personal Info Tab */}
          {activeTab === "personal" && (
            <div className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 md:gap-6">
                {/* ...existing code for main personal info fields... */}
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    First Name
                  </label>
                  <input
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange(
                        "personalInfo",
                        "firstName",
                        e.target.value,
                      )
                    }
                    placeholder="Rametse"
                    type="text"
                    value={profile.personalInfo.firstName}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Last Name
                  </label>
                  <input
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange(
                        "personalInfo",
                        "lastName",
                        e.target.value,
                      )
                    }
                    placeholder="Mothomotsoana"
                    type="text"
                    value={profile.personalInfo.lastName || ""}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Email{" "}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      (from account)
                    </span>
                  </label>
                  <input
                    className="w-full rounded-md border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 sm:p-3 sm:text-base"
                    disabled={true}
                    type="email"
                    value={profile.personalInfo.email}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Phone
                  </label>
                  <input
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange("personalInfo", "phone", e.target.value)
                    }
                    placeholder="+266 5048 0814"
                    type="tel"
                    value={profile.personalInfo.phone}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Address
                  </label>
                  <input
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange(
                        "personalInfo",
                        "address",
                        e.target.value,
                      )
                    }
                    placeholder="Machache 130 St."
                    type="text"
                    value={profile.personalInfo.address}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    City
                  </label>
                  <input
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange("personalInfo", "city", e.target.value)
                    }
                    placeholder="Maseru"
                    type="text"
                    value={profile.personalInfo.city}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    ZIP Code
                  </label>
                  <input
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange(
                        "personalInfo",
                        "zipCode",
                        e.target.value,
                      )
                    }
                    placeholder="130"
                    type="text"
                    value={profile.personalInfo.zipCode}
                  />
                </div>
              </div>

              {/* Personal Info Save Button */}
              {/* Emergency Contact Section */}
              <div className="mt-8 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/30">
                <h4 className="mb-4 text-base font-semibold text-gray-800 dark:text-gray-100">
                  Emergency Contact
                </h4>
                <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 md:gap-6">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                      Name
                    </label>
                    <input
                      className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                      disabled={!isEditing || !isTabEditable()}
                      onChange={(e) =>
                        handleInputChange(
                          "personalInfo",
                          "emergencyContactName",
                          e.target.value,
                        )
                      }
                      placeholder="e.g. Thabo"
                      type="text"
                      value={profile.personalInfo.emergencyContactName || ""}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                      Surname
                    </label>
                    <input
                      className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                      disabled={!isEditing || !isTabEditable()}
                      onChange={(e) =>
                        handleInputChange(
                          "personalInfo",
                          "emergencyContactSurname",
                          e.target.value,
                        )
                      }
                      placeholder="e.g. Mokoena"
                      type="text"
                      value={profile.personalInfo.emergencyContactSurname || ""}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                      Phone
                    </label>
                    <input
                      className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                      disabled={!isEditing || !isTabEditable()}
                      onChange={(e) =>
                        handleInputChange(
                          "personalInfo",
                          "emergencyContactPhone",
                          e.target.value,
                        )
                      }
                      placeholder="e.g. +266 5000 0000"
                      type="tel"
                      value={profile.personalInfo.emergencyContactPhone || ""}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                      Relationship
                    </label>
                    <input
                      className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                      disabled={!isEditing || !isTabEditable()}
                      onChange={(e) =>
                        handleInputChange(
                          "personalInfo",
                          "emergencyContactRelationship",
                          e.target.value,
                        )
                      }
                      placeholder="e.g. Brother, Mother, Friend"
                      type="text"
                      value={
                        profile.personalInfo.emergencyContactRelationship || ""
                      }
                    />
                  </div>
                </div>
              </div>
              {isEditing && (
                <div className="flex justify-end border-t border-gray-200 pt-3 dark:border-gray-700 sm:pt-4">
                  <button
                    className={`rounded-md px-3 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 sm:px-4 sm:py-2 sm:text-sm ${
                      isPersonalInfoValid() && isTabEditable()
                        ? "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
                        : "cursor-not-allowed bg-gray-300 text-gray-500 dark:bg-gray-600 dark:text-gray-400"
                    }`}
                    disabled={!isPersonalInfoValid() || !isTabEditable()}
                    onClick={handleSavePersonal}
                    type="button"
                  >
                    Save Personal Info
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Transport Tab */}
          {activeTab === "transport" && (
            <div className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 md:gap-6">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Make
                  </label>
                  <input
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange("transportInfo", "make", e.target.value)
                    }
                    placeholder="Toyota"
                    type="text"
                    value={profile.transportInfo.make}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Model
                  </label>
                  <input
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange(
                        "transportInfo",
                        "model",
                        e.target.value,
                      )
                    }
                    placeholder="Camry"
                    type="text"
                    value={profile.transportInfo.model}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Year
                  </label>
                  <input
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange("transportInfo", "year", e.target.value)
                    }
                    placeholder="2020"
                    type="text"
                    value={profile.transportInfo.year}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Color
                  </label>
                  <input
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange(
                        "transportInfo",
                        "color",
                        e.target.value,
                      )
                    }
                    placeholder="Silver"
                    type="text"
                    value={profile.transportInfo.color}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    License Plate
                  </label>
                  <input
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange(
                        "transportInfo",
                        "licensePlate",
                        e.target.value,
                      )
                    }
                    placeholder="ABC-1234"
                    type="text"
                    value={profile.transportInfo.licensePlate}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    VIN (Vehicle Identification Number)
                  </label>
                  <input
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange("transportInfo", "vin", e.target.value)
                    }
                    placeholder="1HGBH41JXMN109186"
                    type="text"
                    value={profile.transportInfo.vin}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    {user?.role === "ScooterDriver"
                      ? "Motor Type"
                      : "Vehicle Type"}
                  </label>
                  <select
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange(
                        "transportInfo",
                        "transportType",
                        e.target.value,
                      )
                    }
                    value={profile.transportInfo.transportType}
                  >
                    <option value="">
                      {user?.role === "ScooterDriver"
                        ? "Select motor type"
                        : "Select vehicle type"}
                    </option>
                    {user?.role === "VehicleDriver" ||
                    user?.role === "driver" ? (
                      // Vehicle driver specific options
                      <>
                        <option value="sedan">5 Seater</option>
                        <option value="suv">7 Seater</option>
                      </>
                    ) : user?.role === "ScooterDriver" ? (
                      // Scooter driver specific options
                      <>
                        <option value="scooter">Motorbike</option>
                        <option value="bicycle">Bicycle</option>
                      </>
                    ) : user?.role === "DeliveryDriver" ? (
                      // Scooter driver specific options
                      <>
                        <option value="van">Van</option>
                        <option value="tipper">Tipper</option>
                        <option value="truck">Truck</option>
                        <option value="tow">Tower</option>
                        <option value="suv">7 Seater</option>
                        <option value="sedan">5 Seater</option>
                        <option value="scooter">Motorbike</option>
                        <option value="bicycle">Bicycle</option>
                      </>
                    ): (
                      // Default options for other roles
                      <>
                        <option value="bicycle">Bicycle</option>
                        <option value="scooter">Motorbike</option>
                        <option value="sedan">5 Seater</option>
                        <option value="suv">7 Seater</option>
                        <option value="van">Van</option>
                        <option value="truck">Truck</option>
                        <option value="tipper">Tipper</option>
                        <option value="tow">Tower</option>
                      </>
                    )}
                  </select>
                </div>
                {user?.role === "ScooterDriver" && (
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                      Scooter Experience
                    </label>
                    <select
                      className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                      disabled={!isEditing || !isTabEditable()}
                      onChange={(e) =>
                        handleInputChange(
                          "transportInfo",
                          "scooterExperience",
                          e.target.value,
                        )
                      }
                      value={profile.transportInfo.scooterExperience}
                    >
                      <option value="">Select experience level</option>
                      <option value="beginner">Beginner (0-6 months)</option>
                      <option value="intermediate">
                        Intermediate (6-12 months)
                      </option>
                      <option value="experienced">
                        Experienced (1+ years)
                      </option>
                    </select>
                  </div>
                )}
                {(user?.role === "DeliveryDriver" ||
                  user?.role === "ScooterDriver" ||
                  (user?.role !== "driver" &&
                    user?.role !== "VehicleDriver")) && (
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                      Preferred Delivery Type
                    </label>
                    <select
                      className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                      disabled={!isEditing || !isTabEditable()}
                      onChange={(e) =>
                        handleInputChange(
                          "transportInfo",
                          "preferredDeliveryType",
                          e.target.value,
                        )
                      }
                      value={profile.transportInfo.preferredDeliveryType}
                    >
                      <option value="">Select preferred delivery type</option>
                      <option value="Food Delivery">Food Delivery</option>
                      <option value="Grocery Delivery">Grocery Delivery</option>
                      <option value="Package Delivery">Package Delivery</option>
                      <option value="Pharmacy Delivery">
                        Pharmacy Delivery
                      </option>
                      <option value="All Types">All Types</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Availability
                  </label>
                  <select
                    className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                    disabled={!isEditing || !isTabEditable()}
                    onChange={(e) =>
                      handleInputChange(
                        "transportInfo",
                        "workSchedule",
                        e.target.value,
                      )
                    }
                    value={profile.transportInfo.workSchedule}
                  >
                    <option value="">Select availability</option>
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Flexible">Flexible</option>
                    <option value="Weekends only">Weekends only</option>
                    <option value="Evenings only">Evenings only</option>
                  </select>
                </div>
              </div>

              {/* Vehicle Save Button */}
              {isEditing && (
                <div className="flex justify-end border-t border-gray-200 pt-3 dark:border-gray-700 sm:pt-4">
                  <button
                    className={`rounded-md px-3 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 sm:px-4 sm:py-2 sm:text-sm ${
                      isTransportInfoValid() && isTabEditable()
                        ? "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
                        : "cursor-not-allowed bg-gray-300 text-gray-500 dark:bg-gray-600 dark:text-gray-400"
                    }`}
                    disabled={!isTransportInfoValid() || !isTabEditable()}
                    onClick={handleSaveTransport}
                    type="button"
                  >
                    Save Vehicle Info
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === "preferences" && (
            <div className="space-y-6 sm:space-y-8">
              {/* Preferences */}
              <div>
                <h3 className="mb-3 text-base font-medium text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                  Preferences
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {(
                    [
                      {
                        key: "emailNotifications",
                        label: "Email Notifications",
                      },
                      { key: "pushNotifications", label: "Push Notifications" },
                      { key: "smsNotifications", label: "SMS Notifications" },
                      { key: "emailUpdates", label: "Email Updates" },
                      {
                        key: "notificationsEnabled",
                        label: "Notifications Enabled",
                      },
                      { key: "orderUpdates", label: "Order Updates" },
                      { key: "systemAlerts", label: "System Alerts" },
                      { key: "promotions", label: "Promotions" },
                      { key: "weeklyReports", label: "Weekly Reports" },
                      { key: "twoFactorAuth", label: "Two Factor Auth" },
                      { key: "loginAlerts", label: "Login Alerts" },
                    ] as const
                  ).map(({ key, label }) => (
                    <div
                      className="flex items-center justify-between"
                      key={key}
                    >
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {label}
                      </span>
                      <button
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          (profile.preferences as any)[key]
                            ? "bg-blue-600"
                            : "bg-gray-200 dark:bg-gray-600"
                        } disabled:opacity-50`}
                        disabled={!isEditing}
                        onClick={() => {
                          if (isEditing) {
                            setProfile((prev) => ({
                              ...prev,
                              preferences: {
                                ...prev.preferences,
                                [key]: !(prev.preferences as any)[key],
                              },
                            }));
                          }
                        }}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            (profile.preferences as any)[key]
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                  {/* Numeric preferences */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Session Timeout (min)
                    </span>
                    <input
                      className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      disabled={!isEditing}
                      max={240}
                      min={1}
                      onChange={(e) => {
                        setProfile((prev) => ({
                          ...prev,
                          preferences: {
                            ...prev.preferences,
                            sessionTimeout: Number(e.target.value),
                          },
                        }));
                      }}
                      type="number"
                      value={profile.preferences.sessionTimeout}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Max Devices
                    </span>
                    <input
                      className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      disabled={!isEditing}
                      max={20}
                      min={1}
                      onChange={(e) => {
                        setProfile((prev) => ({
                          ...prev,
                          preferences: {
                            ...prev.preferences,
                            maxDevices: Number(e.target.value),
                          },
                        }));
                      }}
                      type="number"
                      value={profile.preferences.maxDevices}
                    />
                  </div>
                </div>
              </div>

              {/* Working Hours */}
              <div>
                <h3 className="mb-3 text-base font-medium text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                  Working Hours
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                      Start Time
                    </label>
                    <input
                      className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                      disabled={!isEditing}
                      onChange={(e) => {
                        setProfile((prev) => ({
                          ...prev,
                          preferences: {
                            ...prev.preferences,
                            workingHours: {
                              ...prev.preferences.workingHours,
                              start: e.target.value,
                            },
                          },
                        }));
                      }}
                      type="time"
                      value={profile.preferences.workingHours.start}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                      End Time
                    </label>
                    <input
                      className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 sm:p-3 sm:text-base"
                      disabled={!isEditing}
                      onChange={(e) => {
                        setProfile((prev) => ({
                          ...prev,
                          preferences: {
                            ...prev.preferences,
                            workingHours: {
                              ...prev.preferences.workingHours,
                              end: e.target.value,
                            },
                          },
                        }));
                      }}
                      type="time"
                      value={profile.preferences.workingHours.end}
                    />
                  </div>
                </div>
              </div>

              {/* Preferred Areas */}
              <div>
                <h3 className="mb-3 text-base font-medium text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                  Preferred Areas
                </h3>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {profile.preferences.preferredAreas.map((area, index) => (
                    <span
                      className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                      key={index}
                    >
                      {area}
                      {isEditing && (
                        <button
                          className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          onClick={() => {
                            setProfile((prev) => ({
                              ...prev,
                              preferences: {
                                ...prev.preferences,
                                preferredAreas:
                                  prev.preferences.preferredAreas.filter(
                                    (_, i) => i !== index,
                                  ),
                              },
                            }));
                          }}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ))}
                  {isEditing && !showAddAreaInput && (
                    <button
                      className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      onClick={() => setShowAddAreaInput(true)}
                    >
                      + Add Area
                    </button>
                  )}
                  {isEditing && showAddAreaInput && (
                    <div className="inline-flex items-center space-x-2">
                      <input
                        className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                        onChange={(e) => setNewAreaInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter" && newAreaInput.trim()) {
                            setProfile((prev) => ({
                              ...prev,
                              preferences: {
                                ...prev.preferences,
                                preferredAreas: [
                                  ...prev.preferences.preferredAreas,
                                  newAreaInput.trim(),
                                ],
                              },
                            }));
                            setNewAreaInput("");
                            setShowAddAreaInput(false);
                          }
                        }}
                        placeholder="Enter area name"
                        type="text"
                        value={newAreaInput}
                      />
                      <button
                        className="rounded-md bg-blue-600 px-2 py-1 text-sm text-white hover:bg-blue-700"
                        onClick={() => {
                          if (newAreaInput.trim()) {
                            setProfile((prev) => ({
                              ...prev,
                              preferences: {
                                ...prev.preferences,
                                preferredAreas: [
                                  ...prev.preferences.preferredAreas,
                                  newAreaInput.trim(),
                                ],
                              },
                            }));
                            setNewAreaInput("");
                            setShowAddAreaInput(false);
                          }
                        }}
                      >
                        Add
                      </button>
                      <button
                        className="rounded-md bg-gray-500 px-2 py-1 text-sm text-white hover:bg-gray-600"
                        onClick={() => {
                          setNewAreaInput("");
                          setShowAddAreaInput(false);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Preferences Save Button */}
              {isEditing && (
                <div className="flex justify-end border-t border-gray-200 pt-3 dark:border-gray-700 sm:pt-4">
                  <button
                    className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:px-4 sm:py-2 sm:text-sm"
                    onClick={handleSavePreferences}
                    type="button"
                  >
                    Save Preferences
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
